import React, { useState } from "react";
import { motion } from "framer-motion";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import ExportData from "./ExportData";
import { calculateSavingsPotential, categorizeItem } from "../utils/enhancedGuiltAlgorithm"; // Import categorizeItem
import ShareStatsCard from "./ShareStatsCard";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = ({ purchases, clearHistory, persona, goToInput, setCurrentView, theme, userProfile }) => {
  const [filter, setFilter] = useState("all"); // all, low-guilt, high-guilt
  const [showExportModal, setShowExportModal] = useState(false);
  const [viewMode, setViewMode] = useState("stats"); // stats, recent, savings
  
  // No purchases yet
  if (!purchases || purchases.length === 0) {
    return (
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8 text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-6xl mb-4">🛍️</div>
        <h2 className="text-2xl font-bold mb-4 dark:text-white">No Purchase History Yet</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Start logging your shopping adventures to see your Guilt Gauge stats!
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Log Your First Purchase
        </motion.button>
      </motion.div>
    );
  }
  
  // Filter purchases based on selected filter
  const filteredPurchases = () => {
    switch (filter) {
      case "low-guilt":
        return purchases.filter(p => p.guiltScore < 30);
      case "high-guilt":
        return purchases.filter(p => p.guiltScore > 70);
      default:
        return purchases;
    }
  };
  
  // Calculate stats
  const totalSpent = purchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2);
  const avgGuilt = (purchases.reduce((sum, p) => sum + p.guiltScore, 0) / purchases.length).toFixed(1);
  const mostCommonMood = getMostCommonMood(purchases);
  const bestPurchase = [...purchases].sort((a, b) => a.guiltScore - b.guiltScore)[0];
  const worstPurchase = [...purchases].sort((a, b) => b.guiltScore - a.guiltScore)[0];
  
  // Calculate savings potential
  const savingsPotential = calculateSavingsPotential(purchases);
  
  // Get most common mood
  function getMostCommonMood(purchases) {
    const moodCounts = purchases.reduce((acc, p) => {
      acc[p.mood] = (acc[p.mood] || 0) + 1;
      return acc;
    }, {});
    
    // Find the mood with the highest count
    return Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0][0];
  }
  
  // Get mood emoji
  function getMoodEmoji(mood) {
    const emojis = {
      bored: "😑",
      sad: "😢",
      happy: "😊",
      drunk: "🍸",
      celebrating: "🎉",
      cute: "💅",
      fomo: "👀"
    };
    return emojis[mood] || "🛒";
  }
  
  // Prepare chart data
  const chartData = {
    labels: ['0-20', '21-40', '41-60', '61-80', '81-100'],
    datasets: [
      {
        label: 'Number of Purchases',
        data: [
          purchases.filter(p => p.guiltScore >= 0 && p.guiltScore <= 20).length,
          purchases.filter(p => p.guiltScore > 20 && p.guiltScore <= 40).length,
          purchases.filter(p => p.guiltScore > 40 && p.guiltScore <= 60).length,
          purchases.filter(p => p.guiltScore > 60 && p.guiltScore <= 80).length,
          purchases.filter(p => p.guiltScore > 80 && p.guiltScore <= 100).length,
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(255, 99, 132, 0.6)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };
  
  // Define categories that match the enhancedGuiltAlgorithm's categorization
  const mainCategories = [
    { id: 'clothing_fashion', name: 'Clothing', keywords: ['shirt', 'dress', 'pants', 'jacket', 'shoes', 'sweater', 'hoodie', 'jeans', 'coat', 'clothing', 'fashion', 'accessory', 'handbag', 'purse'] },
    { id: 'dining_out', name: 'Food & Dining', keywords: ['dinner', 'lunch', 'breakfast', 'takeout', 'coffee', 'restaurant', 'grocery', 'food', 'meal', 'cafe', 'delivery', 'dining'] },
    { id: 'electronics', name: 'Tech & Gadgets', keywords: ['phone', 'gadget', 'headphone', 'computer', 'laptop', 'tablet', 'camera', 'tech', 'electronic'] },
    { id: 'entertainment', name: 'Entertainment', keywords: ['movie', 'game', 'concert', 'ticket', 'show', 'book', 'music', 'subscription', 'streaming', 'netflix', 'spotify'] },
    { id: 'luxury', name: 'Luxury Items', keywords: ['luxury', 'premium', 'designer', 'high-end', 'expensive', 'branded'] },
    { id: 'home_basics', name: 'Home & Decor', keywords: ['furniture', 'decor', 'appliance', 'home', 'kitchen', 'bedding', 'decoration', 'ornament'] },
    { id: 'cosmetics', name: 'Beauty & Cosmetics', keywords: ['makeup', 'skincare', 'beauty', 'cosmetic', 'perfume', 'cologne', 'fragrance'] },
    { id: 'fitness_basic', name: 'Fitness', keywords: ['exercise', 'fitness', 'gym', 'workout', 'health', 'sport', 'athletic'] }
  ];
  
  // Helper function to get category spending
  function getCategorySpending(purchases, categoryId) {
    // Handle the special "other" category differently
    if (categoryId === "other") {
      // Find purchases that don't match any of our defined categories
      const uncategorizedPurchases = purchases.filter(purchase => {
        if (!purchase.item) return false;
        const itemLowerCase = purchase.item.toLowerCase();
        
        // Check if this purchase doesn't match any category keywords
        return !mainCategories.some(category => 
          category.keywords.some(keyword => itemLowerCase.includes(keyword))
        );
      });
      
      // Sum uncategorized purchases
      return uncategorizedPurchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    }
    
    // For normal categories, get the category definition
    const category = mainCategories.find(cat => cat.id === categoryId);
    if (!category) return 0;
    
    // Filter purchases that match this category
    const matchingPurchases = purchases.filter(purchase => {
      if (!purchase.item) return false;
      const itemLowerCase = purchase.item.toLowerCase();
      
      // Check if item includes any category keywords
      return category.keywords.some(keyword => itemLowerCase.includes(keyword));
    });
    
    // Sum matching purchases
    return matchingPurchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  }
  
  // Add "Other" to the categories array
  const allCategories = [
    ...mainCategories,
    { id: 'other', name: 'Other', keywords: [] } // Special category for uncategorized items
  ];
  
  // Calculate category amounts including "Other"
  const categoryAmounts = allCategories.map(cat => getCategorySpending(purchases, cat.id));
  
  // Filter out categories with zero spending to avoid empty segments
  const nonZeroCategories = allCategories.filter((cat, index) => categoryAmounts[index] > 0);
  const nonZeroAmounts = categoryAmounts.filter(amount => amount > 0);
  
  // Handle the case where there's no spending in any category
  if (nonZeroCategories.length === 0) {
    nonZeroCategories.push({ name: 'No spending data' });
    nonZeroAmounts.push(1); // Add a placeholder value
  }
  
  // Prepare category spending chart data with filtered categories
  const categoryData = {
    labels: nonZeroCategories.map(cat => cat.name),
    datasets: [
      {
        data: nonZeroAmounts,
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(199, 199, 199, 0.6)',
          'rgba(83, 186, 131, 0.6)',
          'rgba(128, 128, 128, 0.6)', // Gray for "Other" category
        ].slice(0, nonZeroCategories.length),
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(199, 199, 199, 1)',
          'rgba(83, 186, 131, 1)',
          'rgba(128, 128, 128, 1)', // Gray for "Other" category
        ].slice(0, nonZeroCategories.length),
        borderWidth: 1,
      },
    ],
  };
  
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Guilt Score Distribution',
        color: theme === 'dark' ? '#fff' : '#333'
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            return `Guilt Score: ${context[0].label}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: theme === 'dark' ? '#ccc' : '#666'
        },
        grid: {
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      },
      x: {
        ticks: {
          color: theme === 'dark' ? '#ccc' : '#666'
        },
        grid: {
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  };
  
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme === 'dark' ? '#ccc' : '#666',
          font: {
            size: 11
          },
          padding: 10,
          boxWidth: 12
        }
      },
      title: {
        display: true,
        text: 'Spending by Category',
        color: theme === 'dark' ? '#fff' : '#333',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: $${value.toFixed(2)} (${percentage}%)`;
          }
        }
      }
    }
  };
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };
  
  // Theme-based accent styling
  const accentStyles = {
    mint: "border-emerald-500 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400",
    pink: "border-pink-500 text-pink-600 dark:border-pink-400 dark:text-pink-400",
    lavender: "border-purple-500 text-purple-600 dark:border-purple-400 dark:text-purple-400"
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Handle navigation to full insights page
  const goToFullInsights = () => {
    setCurrentView("insights");
  };
  
  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold dark:text-white">Your Shopping Dashboard</h2>
        
        <div className="flex space-x-2">
          <motion.button
            onClick={() => setShowExportModal(true)}
            className={`px-3 py-2 rounded-xl text-white text-sm bg-blue-500 hover:bg-blue-600`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Export
          </motion.button>
          
          <motion.button
            onClick={goToInput}
            className={`px-3 py-2 rounded-xl text-white text-sm ${buttonStyles[theme]}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            + Add
          </motion.button>
        </div>
      </div>
      
      {/* View Mode Tabs */}
      <div className="flex mb-6 border-b dark:border-gray-700">
        <button
          onClick={() => setViewMode("stats")}
          className={`px-4 py-2 font-medium rounded-t-lg transition ${
            viewMode === "stats" 
              ? `${accentStyles[theme]} border-b-2` 
              : "text-gray-600 dark:text-gray-400"
          }`}
        >
          Stats
        </button>
        <button
          onClick={() => setViewMode("insights")}
          className={`px-4 py-2 font-medium rounded-t-lg transition ${
            viewMode === "insights" 
              ? `${accentStyles[theme]} border-b-2` 
              : "text-gray-600 dark:text-gray-400"
          }`}
        >
          Insights
        </button>
        <button
          onClick={() => setViewMode("recent")}
          className={`px-4 py-2 font-medium rounded-t-lg transition ${
            viewMode === "recent" 
              ? `${accentStyles[theme]} border-b-2` 
              : "text-gray-600 dark:text-gray-400"
          }`}
        >
          Recent
        </button>
      </div>
      
      {/* Stats View */}
      {viewMode === "stats" && (
        <>
          {/* Stats summary */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
              <div className="text-gray-500 dark:text-gray-400 text-sm">Total Spent</div>
              <div className="text-2xl font-bold dark:text-white">${totalSpent}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
              <div className="text-gray-500 dark:text-gray-400 text-sm">Avg Guilt Score</div>
              <div className="text-2xl font-bold dark:text-white">{avgGuilt}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
              <div className="text-gray-500 dark:text-gray-400 text-sm">Most Common Mood</div>
              <div className="text-2xl font-bold dark:text-white">
                {getMoodEmoji(mostCommonMood)} {mostCommonMood}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
              <div className="text-gray-500 dark:text-gray-400 text-sm">Total Purchases</div>
              <div className="text-2xl font-bold dark:text-white">{purchases.length}</div>
            </div>
          </div>
          
          {/* Impulse Persona - show after 5+ purchases */}
          {persona && purchases.length >= 5 && (
            <motion.div 
              className="bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl p-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold dark:text-white">Your Shopping Personality</h3>
                  <div className="text-xl font-bold mt-1 dark:text-white">{persona}</div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Based on your purchase history and guilt patterns
                  </p>
                </div>
                <div className="text-4xl">✨</div>
              </div>
            </motion.div>
          )}
          
          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <Bar data={chartData} options={chartOptions} height={180} />
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <div className="h-[180px] flex items-center justify-center">
                <Doughnut data={categoryData} options={pieOptions} />
              </div>
              
              {/* Add a debug message if no categories have spending */}
              {nonZeroCategories.length === 1 && nonZeroCategories[0].name === 'No spending data' && (
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
                  None of your purchases match our category keywords yet. Try adding more specific purchase names.
                </p>
              )}
            </div>
          </div>

          {/* Sharing Section */}
          <div className="mb-6">
            <ShareStatsCard 
              purchaseData={{
                item: "dashboard", // This will not be used directly, but helpful for identification
                amount: totalSpent,
                mood: mostCommonMood || "shopping",
                usageFrequency: "regularly",
                guiltScore: Math.round(parseFloat(avgGuilt)) || 50,
                purchaseCount: purchases.length // Added purchase count for dashboard card
              }} 
              theme={theme} 
              userPersonality={persona}
              cardType="dashboard" // Set cardType to "dashboard" for Dashboard stats
            />
          </div>

          {/* New recommendation section */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold dark:text-white">Mindful Shopping Picks</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Discover products aligned with your spending habits
                </p>
              </div>
              <motion.button
                onClick={() => setCurrentView("shop")}
                className={`px-4 py-2 rounded-lg text-white text-sm ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore
              </motion.button>
            </div>
          </div>
          
          {/* Feature Navigation Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <button 
              onClick={() => setCurrentView("goals")}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-start">
                <div className="text-2xl mr-3">🎯</div>
                <div>
                  <h3 className="font-semibold dark:text-white">Spending Goals</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Set and track monthly budgets
                  </p>
                </div>
              </div>
            </button>
            
            <button 
              onClick={() => setCurrentView("insights")}
              className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-start">
                <div className="text-2xl mr-3">🧠</div>
                <div>
                  <h3 className="font-semibold dark:text-white">Shopping Insights</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Detailed analysis of your habits
                  </p>
                </div>
              </div>
            </button>
            
            <button 
              onClick={() => setCurrentView("challenges")}
              className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-start">
                <div className="text-2xl mr-3">🏆</div>
                <div>
                  <h3 className="font-semibold dark:text-white">Challenges</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Complete challenges to improve habits
                  </p>
                </div>
              </div>
            </button>
          </div>
        </>
      )}
      
      {/* Insights View */}
      {viewMode === "insights" && (
        <>
          {/* Savings Potential */}
          {savingsPotential ? (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-2 flex items-center dark:text-white">
                <span className="text-2xl mr-2">💰</span> Savings Potential
              </h3>
              
              <div className="text-lg font-bold mb-3 dark:text-white">
                {formatCurrency(savingsPotential.savingsPotential.yearly)} per year
              </div>
              
              <div className="text-sm">
                <p className="mb-2 dark:text-gray-300">
                  By reducing high-guilt and impulse purchases, you could save:
                </p>
                
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-2">
                    <div className="text-gray-500 dark:text-gray-400 text-xs">Monthly</div>
                    <div className="font-semibold dark:text-white">{formatCurrency(savingsPotential.savingsPotential.monthly)}</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-2">
                    <div className="text-gray-500 dark:text-gray-400 text-xs">In 5 Years</div>
                    <div className="font-semibold dark:text-white">{formatCurrency(savingsPotential.savingsPotential.fiveYear)}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Fallback when savings potential data is not available
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-2 flex items-center dark:text-white">
                <span className="text-2xl mr-2">💰</span> Savings Potential
              </h3>
              <p className="text-sm dark:text-gray-300">
                We need more purchase data to calculate your savings potential. Continue tracking purchases to see how much you could save.
              </p>
            </div>
          )}
          
          {/* Best & Worst Purchases */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {bestPurchase ? (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                <h3 className="font-semibold flex items-center dark:text-white">
                  <span className="mr-2">👑</span> Best Purchase
                </h3>
                <div className="text-lg font-bold mt-1 dark:text-white">{bestPurchase.item}</div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="dark:text-gray-300">${bestPurchase.amount}</span>
                  <span className="text-green-600 dark:text-green-400 font-semibold">
                    Score: {bestPurchase.guiltScore}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <h3 className="font-semibold flex items-center dark:text-white">
                  <span className="mr-2">👑</span> Best Purchase
                </h3>
                <p className="text-sm dark:text-gray-300 mt-1">
                  Keep tracking purchases to identify your lowest guilt items.
                </p>
              </div>
            )}
            
            {worstPurchase ? (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
                <h3 className="font-semibold flex items-center dark:text-white">
                  <span className="mr-2">😬</span> Most Regretted
                </h3>
                <div className="text-lg font-bold mt-1 dark:text-white">{worstPurchase.item}</div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="dark:text-gray-300">${worstPurchase.amount}</span>
                  <span className="text-red-600 dark:text-red-400 font-semibold">
                    Score: {worstPurchase.guiltScore}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                <h3 className="font-semibold flex items-center dark:text-white">
                  <span className="mr-2">😬</span> Most Regretted
                </h3>
                <p className="text-sm dark:text-gray-300 mt-1">
                  Keep tracking purchases to identify your highest guilt items.
                </p>
              </div>
            )}
          </div>
          
          {/* Shopping Wisdom */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-6">
            <h3 className="font-semibold mb-3 dark:text-white">Your Shopping Wisdom</h3>
            
            <div className="space-y-3">
              {purchases.length > 0 ? (
                <>
                  <div className="flex items-start">
                    <div className="text-lg mr-3">🔍</div>
                    <div>
                      <div className="font-medium dark:text-white">Mood Awareness</div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        You shop most often when you are feeling 
                        <span className="font-medium"> {mostCommonMood}</span>. 
                        Being aware of this trigger can help you make more mindful decisions.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="text-lg mr-3">💡</div>
                    <div>
                      <div className="font-medium dark:text-white">Smart Savings</div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        If you invested your average monthly impulse purchases 
                        ($
                        {savingsPotential ? 
                          (savingsPotential.emotional ? savingsPotential.emotional.monthly.toFixed(2) : 
                            (parseFloat(totalSpent) / purchases.length).toFixed(2)) : 
                          (parseFloat(totalSpent) / purchases.length).toFixed(2)
                        }), 
                        you could have an extra 
                        ${savingsPotential ? 
                          (savingsPotential.emotional ? 
                            (savingsPotential.emotional.monthly * 12 * 5 * 1.07).toFixed(2) : 
                            ((parseFloat(totalSpent) / purchases.length) * 12 * 5 * 1.07).toFixed(2)) : 
                          ((parseFloat(totalSpent) / purchases.length) * 12 * 5 * 1.07).toFixed(2)
                        } 
                        in 5 years (at 7% return).
                      </p>
                    </div>
                  </div>
                  
                  {/* Personalized tip based on data */}
                  <div className="flex items-start">
                    <div className="text-lg mr-3">✨</div>
                    <div>
                      <div className="font-medium dark:text-white">Personalized Tip</div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {avgGuilt > 60 ? 
                          "Consider implementing a 24-hour cooling off period before completing purchases to reduce guilt." :
                          avgGuilt < 30 ?
                          "You generally make guilt-free purchases! Keep using this mindful approach for future shopping." :
                          "Try categorizing your spending into 'needs' vs 'wants' to maintain a healthy balance."
                        }
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Start tracking your purchases to gain shopping wisdom and personalized insights.
                </p>
              )}
            </div>
          </div>
          
          {/* View Full Insights Button */}
          <div className="text-center">
            <motion.button
              onClick={goToFullInsights}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              View Detailed Insights
            </motion.button>
          </div>
        </>
      )}
      
      {/* Recent Purchases View */}
      {viewMode === "recent" && (
        <>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold dark:text-white">Recent Purchases</h3>
            <div className="flex space-x-2">
              <button 
                onClick={() => setFilter("all")}
                className={`text-xs px-2 py-1 rounded-full ${filter === "all" ? `${accentStyles[theme]} border` : "bg-gray-100 dark:bg-gray-700 dark:text-gray-300"}`}
              >
                All
              </button>
              <button 
                onClick={() => setFilter("low-guilt")}
                className={`text-xs px-2 py-1 rounded-full ${filter === "low-guilt" ? `${accentStyles[theme]} border` : "bg-gray-100 dark:bg-gray-700 dark:text-gray-300"}`}
              >
                Guilt-Free
              </button>
              <button 
                onClick={() => setFilter("high-guilt")}
                className={`text-xs px-2 py-1 rounded-full ${filter === "high-guilt" ? `${accentStyles[theme]} border` : "bg-gray-100 dark:bg-gray-700 dark:text-gray-300"}`}
              >
                Regret List
              </button>
            </div>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto mb-6">
            {filteredPurchases().map((purchase) => (
              <div 
                key={purchase.id} 
                className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-xl p-3"
              >
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    purchase.guiltScore < 30 ? "bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400" :
                    purchase.guiltScore < 60 ? "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/50 dark:text-yellow-400" :
                    "bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400"
                  }`}>
                    {purchase.guiltScore}
                  </div>
                  <div>
                    <div className="font-medium dark:text-white">{purchase.item}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ${purchase.amount} • {new Date(purchase.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="text-xl">{getMoodEmoji(purchase.mood)}</div>
              </div>
            ))}
          </div>
        </>
      )}
      
      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3">
        <motion.button
          onClick={goToInput}
          className="py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold dark:text-white"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Add New Purchase
        </motion.button>
        
        <motion.button
          onClick={clearHistory}
          className="py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-red-500 dark:text-red-400 font-semibold"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Clear History
        </motion.button>
      </div>
      
      {/* Fun quote */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 italic mt-6">
        {purchases.length > 10 ? 
          "Maybe don't open your banking app right now..." :
          "Every purchase tells a story. Not all are happy endings."
        }
      </div>
      
      {/* Export Modal */}
      {showExportModal && (
        <ExportData 
          purchases={purchases}
          theme={theme}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </motion.div>
  );
};

export default Dashboard;
